import { Error } from "@mui/icons-material";
import { Grid, Typography, useTheme } from "@mui/material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";
import { answerColorToThemeColor } from "components/Evaluation/Utils";
import { observer } from "mobx-react";
import { TestLLMClassifierAgreementResponse } from "models/LLMs/Testing/TestLLMClassifierAgreementResponse";
import React from "react";
import { useStore } from "utils/useStore";
import { getFriendlyId } from "../utils";
import { SuccessfulResultsSection } from "./SuccessfulResultsSection";
import { AgreementMatrix } from "./AgreementMatrix";
import { ErrorResultsSection } from "./ErrorResultsSection";
import { ScrollToResultButton } from "../ScrollToResultButton";
import { ExportToCSV } from "components/CSVExport/ExportToCSV";

interface Props {
    data: TestLLMClassifierAgreementResponse;
}

export const AgreementTestingResults = observer(({ data }: Props) => {
    const theme = useTheme();
    const store = useStore(LLMClassifierBuilderStore);

    const outputs = store.classifier.outputs;
    const getLabelResults = (label: string) => data.labelToResults[label] ?? [];

    const sections = outputs
        .map((output) => ({
            label: output.result,
            results: getLabelResults(output.result),
            colors: answerColorToThemeColor(
                theme,
                store.getResultColor(output.result),
            ),
        }))
        .concat({
            label: "No Agreement",
            results: data.noAgreementResults
                .slice()
                .sort((a, b) =>
                    a.conversation.labeled.localeCompare(
                        b.conversation.labeled,
                    ),
                ),
            colors: answerColorToThemeColor(theme),
        })
        .sort((a, b) => b.results.length - a.results.length);

    const hasErrors = data.errorResults.length > 0;
    const errorsSection = {
        label: "Errors",
        results: data.errorResults,
        colors: {
            color: theme.palette.red.main,
            borderColor: theme.palette.red.main,
            backgroundColor: theme.palette.red.background,
        },
    };

    return (
        <Grid container direction="column" flexWrap="nowrap" width="100%">
            <Grid item>
                <TitledSection
                    title={
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Grid item>Results ({data.processedCount})</Grid>
                            <Grid item>
                                <ExportToCSV
                                    data={Object.values(data.labelToResults)
                                        .flat()
                                        .concat(
                                            data.noAgreementResults,
                                            data.errorResults,
                                        )}
                                    selectedKeys={store.selectedExportKeys}
                                    onSelectedKeysChange={(keys) =>
                                        store.setSelectedExportKeys(keys)
                                    }
                                    getLabelForKey={(key) => {
                                        switch (key) {
                                            case "conversation.labeled":
                                                return "Human Label";
                                            case "decision":
                                                return "Model Label";
                                            case "reasoning":
                                                return "Model Reasoning";
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    }
                    variant="bordered"
                >
                    <Grid
                        container
                        direction="column"
                        flexWrap="nowrap"
                        rowGap={1}
                        paddingBlock={2}
                        paddingInline={2}
                    >
                        <TitledSection title="Agreement" variant="bordered">
                            <Typography>Agreed</Typography>
                            <Grid container justifyContent="space-between">
                                <Grid item>
                                    <Typography
                                        sx={(theme) => ({
                                            color: theme.palette.gray[500],
                                        })}
                                    >
                                        {data.agreementPercent}%
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        sx={(theme) => ({
                                            color: theme.palette.gray[500],
                                        })}
                                    >
                                        {100 - data.agreementPercent}%
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container width="100%">
                                <Grid
                                    item
                                    sx={(theme) => ({
                                        width: `${data.agreementPercent}%`,
                                        height: theme.spacing(4),
                                        backgroundColor:
                                            theme.palette.primary[800],
                                        borderTopLeftRadius:
                                            theme.shape.borderRadius,
                                        borderBottomLeftRadius:
                                            theme.shape.borderRadius,
                                        borderTopRightRadius:
                                            data.agreementPercent === 100
                                                ? theme.shape.borderRadius
                                                : 0,
                                        borderBottomRightRadius:
                                            data.agreementPercent === 100
                                                ? theme.shape.borderRadius
                                                : 0,
                                    })}
                                />
                                <Grid
                                    item
                                    sx={(theme) => ({
                                        width: `${
                                            100 - data.agreementPercent
                                        }%`,
                                        height: theme.spacing(4),
                                        backgroundColor:
                                            theme.palette.gray[200],
                                        borderTopRightRadius:
                                            theme.shape.borderRadius,
                                        borderBottomRightRadius:
                                            theme.shape.borderRadius,
                                        borderTopLeftRadius:
                                            100 - data.agreementPercent === 100
                                                ? theme.shape.borderRadius
                                                : 0,
                                        borderBottomLeftRadius:
                                            100 - data.agreementPercent === 100
                                                ? theme.shape.borderRadius
                                                : 0,
                                    })}
                                />
                            </Grid>
                            <AgreementMatrix
                                labels={outputs.map((label) => label.result)}
                                getAgreementCountAndPercent={(human, model) => {
                                    let count = 0;

                                    const isInAgreement = human === model;
                                    if (isInAgreement) {
                                        count = getLabelResults(human).length;
                                    } else {
                                        count = data.noAgreementResults.filter(
                                            (result) =>
                                                result.decision === model &&
                                                result.conversation.labeled ===
                                                    human,
                                        ).length;
                                    }

                                    return [
                                        count,
                                        (count / data.processedCount) * 100,
                                    ];
                                }}
                            />
                            <Grid
                                container
                                width="100%"
                                flexWrap="nowrap"
                                columnGap={2}
                                marginTop={2}
                            >
                                {hasErrors && (
                                    <ScrollToResultButton
                                        title={
                                            <Grid
                                                container
                                                alignItems="center"
                                                columnGap={1}
                                                color={
                                                    errorsSection.colors.color
                                                }
                                            >
                                                <Error color="error" />
                                                {errorsSection.label}
                                            </Grid>
                                        }
                                        count={errorsSection.results.length}
                                        scrollToId={getFriendlyId(
                                            errorsSection.label,
                                        )}
                                        sx={{
                                            border: `1px solid ${errorsSection.colors.borderColor}`,
                                            backgroundColor:
                                                errorsSection.colors
                                                    .backgroundColor,
                                        }}
                                    />
                                )}
                                {sections.map((section) => {
                                    return (
                                        <ScrollToResultButton
                                            key={section.label}
                                            title={section.label}
                                            count={section.results.length}
                                            scrollToId={getFriendlyId(
                                                section.label,
                                            )}
                                            sx={{
                                                border: `1px solid ${section.colors.borderColor}`,
                                                backgroundColor:
                                                    section.colors
                                                        .backgroundColor,
                                            }}
                                        />
                                    );
                                })}
                            </Grid>
                        </TitledSection>
                        <ErrorResultsSection results={errorsSection.results} />
                        {sections.map((section) => (
                            <SuccessfulResultsSection
                                key={section.label}
                                results={section.results}
                                label={section.label}
                            />
                        ))}
                    </Grid>
                </TitledSection>
            </Grid>
        </Grid>
    );
});
